import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { database } from '../../Firebase';
import { ref, push, onValue, remove, update } from 'firebase/database';
import { Trash, Pencil, Printer } from 'lucide-react';

const BirthRegistration = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { grampanchayatName, arthikVarshLabel } = location.state || {};

  const [view, setView] = useState('list'); // 'list' or 'form'
  const [searchTerm, setSearchTerm] = useState('');
  const [advanceSearch, setAdvanceSearch] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  
  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Print State
  const [isPrinting, setIsPrinting] = useState(false);
  const [printData, setPrintData] = useState(null);

  // Form State
  const initialFormState = {
    registrationNo: '',
    registrationDate: new Date().toISOString().split('T')[0],
    childDOB: '',
    childSex: '',
    childWeight: '',
    childName: '',
    placeOfBirth: '',
    fatherName: '',
    fatherEducation: '',
    fatherOccupation: '',
    motherName: '',
    motherEducation: '',
    motherOccupation: '',
    motherAgeMarriage: '',
    motherAgeBirth: '',
    addressPermanent: '',
    addressBirth: '',
    informantName: '',
    informantAddress: '',
    remark: ''
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    const birthsRef = ref(database, 'Grampanchayat/Asthapan/Nondani/Birth Registration');
    const unsubscribe = onValue(birthsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const loadRegistrations = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setRegistrations(loadRegistrations);
      } else {
        setRegistrations([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = () => {
    if (!formData.childName || !formData.registrationNo) {
      alert("Please fill in required fields (Child Name, Registration No)");
      return;
    }

    if (isEditing && selectedId) {
      const recordRef = ref(database, `Grampanchayat/Asthapan/Nondani/Birth Registration/${selectedId}`);
      update(recordRef, formData)
        .then(() => {
          alert("Birth Registration Updated Successfully!");
          resetForm();
        })
        .catch((error) => {
          console.error("Error updating data: ", error);
          alert("Error updating data");
        });
    } else {
      const birthsRef = ref(database, 'Grampanchayat/Asthapan/Nondani/Birth Registration');
      push(birthsRef, formData)
        .then(() => {
            alert("Birth Registration Saved Successfully!");
            resetForm();
        })
        .catch((error) => {
          console.error("Error saving data: ", error);
          alert("Error saving data");
        });
    }
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setSelectedId(null);
    setView('list');
  };

  const handleEdit = (record) => {
    setFormData(record);
    setSelectedId(record.id);
    setIsEditing(true);
    setView('form');
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      const recordRef = ref(database, `Grampanchayat/Asthapan/Nondani/Birth Registration/${id}`);
      remove(recordRef)
        .then(() => {
          alert("Record deleted successfully");
        })
        .catch((error) => {
          console.error("Error deleting record: ", error);
          alert("Error deleting record");
        });
    }
  };

  const handlePrint = (record) => {
    setPrintData(record);
    setIsPrinting(true);
    // Slight delay to ensure content is rendered before printing
    setTimeout(() => {
        window.print();
        // Reset after print dialog closes (users might cancel, but we want to return to list)
        // Note: checking if print was cancelled is tricky, so we usually provide a "Close" button in the print view or auto-close
    }, 500);
  };

  const filteredRegistrations = registrations.filter(reg => 
    reg.childName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.registrationNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.fatherName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.motherName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Printable Certificate Component
  if (isPrinting && printData) {
    return (
        <div className="fixed inset-0 bg-white z-50 p-8 overflow-auto">
             <div className="max-w-3xl mx-auto border-4 border-double border-gray-800 p-8 relative">
                {/* Close Button for Web View */}
                <button 
                    onClick={() => setIsPrinting(false)}
                    className="absolute top-4 right-4 print:hidden px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
                >
                    Close
                </button>

                {/* Certificate Header */}
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">जन्म प्रमाणपत्र (Birth Certificate)</h2>
                    <h3 className="text-xl font-semibold text-gray-800 mb-1">महाराष्ट्र शासन (Government of Maharashtra)</h3>
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">{grampanchayatName || 'ग्रामपंचायत'}</h3>
                    <p className="text-sm text-gray-600 italic">
                        (जन्म व मृत्यू नोंदणी अधिनियम, 1969 कलम 12 / 17 आणि महाराष्ट्र जन्म व मृत्यू नोंदणी नियम, 2000 चे नियम 8 / 13 अन्वये देण्यात आले आहे.)
                    </p>
                    <p className="text-sm text-gray-600 italic mt-1">
                        (Issued under Section 12/17 of the Registration of Births & Deaths Act, 1969 and Rule 8/13 of the Maharashtra Registration of Births & Deaths Rules, 2000.)
                    </p>
                </div>

                {/* Certificate Body */}
                <div className="mb-8 leading-relaxed text-gray-800">
                    <p className="mb-4">
                        प्रमाणित करण्यात येते की, खालील माहिती जन्म नोंदवहीच्या मूळ नोंदीवरून घेण्यात आली आहे, जी 
                        <span className="font-bold mx-2">{grampanchayatName || '___'}</span> 
                        तालुका <span className="font-bold mx-2">___</span> 
                        जिल्हा <span className="font-bold mx-2">___</span> 
                        राज्याच्या जन्म नोंदवहीत उल्लेख आहे.
                    </p>
                    <p className="mb-6">
                        (This is to certify that the following information has been taken from the original record of birth which is the register for 
                        <span className="font-bold mx-2">{grampanchayatName || '___'}</span> 
                        Taluka <span className="font-bold mx-2">___</span> 
                        District <span className="font-bold mx-2">___</span> 
                        of State/Union Territory.)
                    </p>

                    <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-lg">
                        <div>
                            <span className="font-semibold block text-sm text-gray-600">नाव (Name):</span>
                            <span className="font-bold">{printData.childName}</span>
                        </div>
                        <div>
                            <span className="font-semibold block text-sm text-gray-600">लिंग (Sex):</span>
                            <span>{printData.childSex === 'Male' ? 'पुरुष (Male)' : 'स्त्री (Female)'}</span>
                        </div>
                        <div>
                            <span className="font-semibold block text-sm text-gray-600">जन्म दिनांक (Date of Birth):</span>
                            <span>{printData.childDOB}</span>
                        </div>
                        <div>
                            <span className="font-semibold block text-sm text-gray-600">जन्म ठिकाण (Place of Birth):</span>
                            <span>{printData.placeOfBirth}</span>
                        </div>
                        <div className="col-span-2">
                             <span className="font-semibold block text-sm text-gray-600">वडीलांचे पूर्ण नाव (Name of Mother/Father):</span>
                             <span>{printData.fatherName}</span>
                        </div>
                        <div className="col-span-2">
                             <span className="font-semibold block text-sm text-gray-600">आईचे पूर्ण नाव (Name of Mother):</span>
                             <span>{printData.motherName}</span>
                        </div>
                        <div className="col-span-2">
                            <span className="font-semibold block text-sm text-gray-600">बाळाच्या जन्मावेळचा पालकांचा पत्ता (Address of Parents at the time of Birth):</span>
                            <span>{printData.addressBirth}</span>
                        </div>
                         <div className="col-span-2">
                            <span className="font-semibold block text-sm text-gray-600">पालकांचा कायमचा पत्ता (Permanent Address of Parents):</span>
                            <span>{printData.addressPermanent}</span>
                        </div>
                         <div>
                            <span className="font-semibold block text-sm text-gray-600">नोंदणी क्रमांक (Registration No):</span>
                            <span>{printData.registrationNo}</span>
                        </div>
                         <div>
                            <span className="font-semibold block text-sm text-gray-600">नोंदणी दिनांक (Date of Registration):</span>
                            <span>{printData.registrationDate}</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-16 pt-8 flex justify-between items-end">
                    <div className="text-center">
                        <p className="border-t border-gray-400 px-4 pt-2">दिनांक (Date)</p>
                    </div>
                    <div className="text-center">
                         <div className="h-16 mb-2"></div> {/* Signature Space */}
                        <p className="border-t border-gray-400 px-4 pt-2 font-bold">निबंधक (जन्म व मृत्यू)</p>
                        <p className="text-sm">Registrar (Birth & Death)</p>
                        <p className="font-bold">{grampanchayatName}</p>
                    </div>
                </div>

             </div>
        </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8 font-poppins">
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-[#267c77] rounded-lg shadow-sm p-4 flex justify-between items-center text-white">
            <div>
                 <h1 className="text-xl font-bold">
                    जन्म नोंदणी यादी {isEditing ? '(संपादन सुरू)' : ''}
                 </h1>
            </div>
            <div className="flex gap-2">
                 <button 
                   onClick={() => navigate('/asthapan/registrations', { state: location.state })}
                   className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition"
                 >
                   मागे जा
                 </button>
                 {view === 'list' && (
                   <button 
                     onClick={() => {
                        resetForm();
                        setView('form');
                     }}
                     className="px-4 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition"
                   >
                     नविन
                   </button>
                 )}
            </div>
        </div>
      </div>

      {view === 'list' ? (
        /* LIST VIEW */
        <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          
          {/* Search Bar */}
          <div className="p-4 border-b border-gray-200">
             <div className="bg-[#267c77] p-2 rounded-t text-white flex items-center gap-2 mb-2">
                <input 
                    type="checkbox" 
                    id="advanceSearch"
                    checked={advanceSearch}
                    onChange={(e) => setAdvanceSearch(e.target.checked)}
                    className="w-4 h-4"
                />
                <label htmlFor="advanceSearch" className="text-white font-medium">अधिक शोध (Advance Search)</label>
             </div>
             <div className="flex gap-4">
                <div className="relative flex-1">
                    <input 
                    type="text" 
                    placeholder="नोंदणी नं./ वडीलांचे नाव / आईचे नाव" 
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#267c77]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="px-6 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition">
                    शोधणे
                </button>
             </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#267c77] text-white">
                  <th className="p-3 border-r border-white/30 font-medium text-center">नोंदणी क्र.</th>
                  <th className="p-3 border-r border-white/30 font-medium text-center">नोंदणी नं.</th>
                  <th className="p-3 border-r border-white/30 font-medium text-center">नोंदणी दिनांक</th>
                  <th className="p-3 border-r border-white/30 font-medium text-center">जन्म दिनांक</th>
                  <th className="p-3 border-r border-white/30 font-medium">वडीलांचे नाव</th>
                  <th className="p-3 border-r border-white/30 font-medium">आईचे नाव</th>
                  <th className="p-3 font-medium text-center">पर्याय</th>
                </tr>
              </thead>
              <tbody>
                  {filteredRegistrations.length > 0 ? (
                    filteredRegistrations.map((reg, index) => (
                      <tr key={reg.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 text-center border-r">{index + 1}</td>
                          <td className="p-3 text-center border-r">{reg.registrationNo}</td>
                          <td className="p-3 text-center border-r">{reg.registrationDate}</td>
                          <td className="p-3 text-center border-r">{reg.childDOB}</td>
                          <td className="p-3 border-r">{reg.fatherName}</td>
                          <td className="p-3 border-r">{reg.motherName}</td>
                          <td className="p-3 text-center flex justify-center gap-2">
                               <button 
                                onClick={() => handleEdit(reg)}
                                className="px-3 py-1 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] text-sm font-medium"
                              >
                                बदल
                              </button>
                              <button 
                                onClick={() => handleDelete(reg.id)}
                                className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
                              >
                                खोडणे
                              </button>
                               <button 
                                onClick={() => handlePrint(reg)}
                                className="px-3 py-1 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] text-sm font-medium"
                              >
                                दाखला
                              </button>
                              <button 
                                className="px-3 py-1 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] text-sm font-medium"
                              >
                                अहवाल
                              </button>
                          </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                        <td colSpan="7" className="p-4 text-center text-gray-500 bg-white">
                            No records found
                        </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>

           {/* Pagination */}
           <div className="p-2 border-t bg-[#267c77] text-white flex justify-center items-center gap-2">
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">|&lt;</button>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&lt;&lt;</button>
                <span className="px-3 py-1 bg-white/20 rounded text-sm bg-[#1e6b66]">1</span>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;&gt;</button>
                <button className="px-2 py-1 bg-white/20 rounded hover:bg-white/30 text-sm">&gt;|</button>
                <select className="ml-2 bg-white text-black text-sm p-1 rounded">
                    <option>5</option>
                    <option>10</option>
                    <option>20</option>
                </select>
           </div>
        </div>
      ) : (
        /* FORM VIEW */
        <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md p-6 border-t-4 border-[#267c77]">
             <div className="mb-6 border-b pb-2">
                 <h2 className="text-xl font-medium text-white bg-[#267c77] inline-block px-4 py-1 rounded-t-lg w-full">
                    {isEditing ? 'जन्म माहिती संपादित करा' : 'जन्म माहिती'}
                 </h2>
             </div>
            <div className="space-y-6">
                
                {/* Registration Details */}
                <h3 className="font-semibold text-[#267c77] border-b pb-1">नोंदणी तपशील</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">नोंदणी क्रमांक</label>
                        <input type="text" name="registrationNo" value={formData.registrationNo} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">नोंदणी दिनांक</label>
                        <input type="date" name="registrationDate" value={formData.registrationDate} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                </div>

                {/* Child Details */}
                <h3 className="font-semibold text-[#267c77] border-b pb-1">बालकाची माहिती</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">जन्म दिनांक</label>
                        <input type="date" name="childDOB" value={formData.childDOB} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">लिंग</label>
                        <select name="childSex" value={formData.childSex} onChange={handleInputChange} className="w-full p-2 border rounded">
                            <option value="">निवडा</option>
                            <option value="Male">मुलगा</option>
                            <option value="Female">मुलगी</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">जन्म वेळचे वजन (kg)</label>
                        <input type="number" name="childWeight" value={formData.childWeight} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div className="md:col-span-2 lg:col-span-4">
                        <label className="block text-gray-700 text-sm font-medium mb-1">बालकाचे नाव</label>
                        <input type="text" name="childName" value={formData.childName} onChange={handleInputChange} className="w-full p-2 border rounded" placeholder="बाळाचे नाव" />
                    </div>
                    <div className="md:col-span-2 lg:col-span-4">
                        <label className="block text-gray-700 text-sm font-medium mb-1">जन्म ठिकाण</label>
                        <input type="text" name="placeOfBirth" value={formData.placeOfBirth} onChange={handleInputChange} className="w-full p-2 border rounded" placeholder="रुग्णालयाचे नाव / घराचा पत्ता" />
                    </div>
                </div>

                 {/* Father's Details */}
                <h3 className="font-semibold text-[#267c77] border-b pb-1">वडीलांची माहिती</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-3">
                        <label className="block text-gray-700 text-sm font-medium mb-1">वडीलांचे पूर्ण नाव</label>
                        <input type="text" name="fatherName" value={formData.fatherName} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">शिक्षण</label>
                         <input type="text" name="fatherEducation" value={formData.fatherEducation} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">व्यवसाय</label>
                         <input type="text" name="fatherOccupation" value={formData.fatherOccupation} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                </div>

                 {/* Mother's Details */}
                 <h3 className="font-semibold text-[#267c77] border-b pb-1">आईची माहिती</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-3">
                        <label className="block text-gray-700 text-sm font-medium mb-1">आईचे पूर्ण नाव</label>
                        <input type="text" name="motherName" value={formData.motherName} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">शिक्षण</label>
                         <input type="text" name="motherEducation" value={formData.motherEducation} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">व्यवसाय</label>
                         <input type="text" name="motherOccupation" value={formData.motherOccupation} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">लग्नाचे वेळचे वय</label>
                         <input type="number" name="motherAgeMarriage" value={formData.motherAgeMarriage} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">प्रसूती वेळी वय</label>
                         <input type="number" name="motherAgeBirth" value={formData.motherAgeBirth} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                </div>

                 {/* Address Details */}
                <h3 className="font-semibold text-[#267c77] border-b pb-1">पत्ता</h3>
                <div className="grid grid-cols-1 gap-4">
                    <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">कायमचा पत्ता</label>
                        <textarea name="addressPermanent" value={formData.addressPermanent} onChange={handleInputChange} className="w-full p-2 border rounded" rows="2"></textarea>
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">बाळ जन्मावेळचा पत्ता</label>
                        <textarea name="addressBirth" value={formData.addressBirth} onChange={handleInputChange} className="w-full p-2 border rounded" rows="2"></textarea>
                    </div>
                </div>

                {/* Informant Details */}
                <h3 className="font-semibold text-[#267c77] border-b pb-1">माहिती देणाऱ्याचा तपशील</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">नाव</label>
                        <input type="text" name="informantName" value={formData.informantName} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                     <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1">पत्ता</label>
                        <input type="text" name="informantAddress" value={formData.informantAddress} onChange={handleInputChange} className="w-full p-2 border rounded" />
                    </div>
                </div>

            </div>

             {/* Footer Buttons */}
             <div className="mt-8 flex justify-center gap-4 py-4 bg-[#267c77] rounded-b-lg -mx-6 -mb-6">
                <button 
                  onClick={handleSave}
                  className="px-6 py-2 bg-[#267c77] text-white rounded hover:bg-[#1e6b66] transition font-medium"
                >
                    {isEditing ? 'अपडेट करा' : 'साठविणे'}
                </button>
                <button 
                  onClick={() => setView('list')}
                  className="px-6 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition font-medium"
                >
                    रद्द करणे
                </button>
             </div>
        </div>
      )}

    </div>
  );
};

export default BirthRegistration;
